import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Collapse,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Grid,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tab,
  Tabs,
  ToggleButton,
  ToggleButtonGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { formatDateTimeInAppTimeZone } from '../utils/dateTime';
import { buildPositionPriceChartData } from '../utils/positionPriceChart';
import { CryptoPriceChart } from './CryptoAnalysis';
import PriceAlerts from './PriceAlerts';
import TickerNotes from './TickerNotes';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL ?? '';
const PAPER_PENDING_ORDER_TYPES = ['limit', 'stop', 'stop_limit', 'trailing_stop'];
const CHART_INTERVAL_OPTIONS = [
  { label: '1D', value: '5' },
  { label: '5D', value: '15' },
  { label: '1M', value: '60' },
  { label: '3M', value: 'D' },
  { label: '1Y', value: 'W' },
  { label: '5Y', value: 'M' },
];
const CHART_STYLE_OPTIONS = [
  { label: 'Candles', value: '1' },
  { label: 'Heikin Ashi', value: '8' },
  { label: 'Hollow Candles', value: '9' },
  { label: 'Line', value: '2' },
  { label: 'Area', value: '3' },
  { label: 'Bars', value: '0' },
];
// TradingView renders volume natively, so 'volume' toggles hidevolume instead of adding a duplicate study.
const CHART_VOLUME_OPTION = 'volume';
const CHART_STUDY_OPTIONS = [
  { label: 'Volume', value: CHART_VOLUME_OPTION },
  { label: 'SMA', value: 'MASimple@tv-basicstudies' },
  { label: 'EMA', value: 'MAExp@tv-basicstudies' },
  { label: 'RSI', value: 'RSI@tv-basicstudies' },
  { label: 'MACD', value: 'MACD@tv-basicstudies' },
  { label: 'Bollinger Bands', value: 'BB@tv-basicstudies' },
  { label: 'VWAP', value: 'VWAP@tv-basicstudies' },
];
const FALLBACK_CRYPTO_OPTIONS = [
  { id: 'bitcoin', symbol: 'BTC-USD', baseSymbol: 'BTC', name: 'Bitcoin', tradingViewSymbol: 'COINBASE:BTCUSD', hasTradingView: true },
  { id: 'ethereum', symbol: 'ETH-USD', baseSymbol: 'ETH', name: 'Ethereum', tradingViewSymbol: 'COINBASE:ETHUSD', hasTradingView: true },
  { id: 'solana', symbol: 'SOL-USD', baseSymbol: 'SOL', name: 'Solana', tradingViewSymbol: 'COINBASE:SOLUSD', hasTradingView: true },
  { id: 'ripple', symbol: 'XRP-USD', baseSymbol: 'XRP', name: 'XRP', tradingViewSymbol: 'COINBASE:XRPUSD', hasTradingView: true },
  { id: 'cardano', symbol: 'ADA-USD', baseSymbol: 'ADA', name: 'Cardano', tradingViewSymbol: 'COINBASE:ADAUSD', hasTradingView: true },
  { id: 'dogecoin', symbol: 'DOGE-USD', baseSymbol: 'DOGE', name: 'Dogecoin', tradingViewSymbol: 'COINBASE:DOGEUSD', hasTradingView: true },
  { id: 'avalanche-2', symbol: 'AVAX-USD', baseSymbol: 'AVAX', name: 'Avalanche', tradingViewSymbol: 'COINBASE:AVAXUSD', hasTradingView: true },
  { id: 'chainlink', symbol: 'LINK-USD', baseSymbol: 'LINK', name: 'Chainlink', tradingViewSymbol: 'COINBASE:LINKUSD', hasTradingView: true },
  { id: 'litecoin', symbol: 'LTC-USD', baseSymbol: 'LTC', name: 'Litecoin', tradingViewSymbol: 'COINBASE:LTCUSD', hasTradingView: true },
  { id: 'bitcoin-cash', symbol: 'BCH-USD', baseSymbol: 'BCH', name: 'Bitcoin Cash', tradingViewSymbol: 'COINBASE:BCHUSD', hasTradingView: true },
];
const VERIFIED_TRADINGVIEW_CRYPTO = new Map(FALLBACK_CRYPTO_OPTIONS.map((option) => [option.symbol, option]));
const POSITION_PRICE_COLORS = ['#2563eb', '#10b981', '#f59e0b', '#8b5cf6', '#ef4444', '#06b6d4', '#84cc16', '#f97316', '#ec4899', '#14b8a6'];

const isCryptoTicker = (ticker) => /^[A-Z0-9]{1,15}-USD$/.test(String(ticker || '').trim().toUpperCase());

const buildCryptoOptions = (coins) => {
  const optionsBySymbol = new Map();

  (Array.isArray(coins) ? coins : []).forEach((coin) => {
    const baseSymbol = String(coin?.symbol || '').trim().toUpperCase();
    if (!/^[A-Z0-9]{1,15}$/.test(baseSymbol)) return;

    const symbol = `${baseSymbol}-USD`;
    if (!optionsBySymbol.has(symbol)) {
      const verifiedOption = VERIFIED_TRADINGVIEW_CRYPTO.get(symbol);
      optionsBySymbol.set(symbol, {
        id: String(coin?.id || '').trim(),
        symbol,
        baseSymbol,
        name: String(coin?.name || baseSymbol).trim(),
        tradingViewSymbol: verifiedOption?.tradingViewSymbol || '',
        hasTradingView: Boolean(verifiedOption),
      });
    }
  });

  FALLBACK_CRYPTO_OPTIONS.forEach((option) => {
    if (!optionsBySymbol.has(option.symbol)) optionsBySymbol.set(option.symbol, option);
  });

  return [...optionsBySymbol.values()];
};

const currency = (value) => {
  if (!Number.isFinite(value)) {
    return '-';
  }
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
};

const number = (value, digits = 4) => {
  if (!Number.isFinite(value)) {
    return '-';
  }
  return Number(value).toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: digits,
  });
};

const percent = (value, digits = 2) => {
  if (!Number.isFinite(value)) {
    return '-';
  }
  return `${Number(value).toFixed(digits)}%`;
};

const identityLabel = (entry) => {
  const userId = String(entry?.user_id || '').trim();
  const email = String(entry?.email || '').trim();
  const displayName = String(entry?.display_name || '').trim();

  if (displayName && email) return `${displayName} (${email})`;
  if (displayName && userId) return `${displayName} (${userId})`;
  if (email) return email;
  return userId || 'Unknown';
};

const calculateReturnPct = (summary) => {
  const startingCash = Number(summary?.account?.starting_cash);
  const totalPnl = Number(summary?.metrics?.total_pnl);
  if (!Number.isFinite(startingCash) || startingCash <= 0 || !Number.isFinite(totalPnl)) {
    return null;
  }
  return (totalPnl / startingCash) * 100;
};

const calculateOrderTotal = (order) => {
  const qty = Number(order?.quantity);
  const fillPrice = Number(order?.filled_price);
  const requestedPrice = Number(order?.requested_price);

  if (!Number.isFinite(qty) || qty <= 0) {
    return null;
  }

  if (Number.isFinite(fillPrice) && fillPrice > 0) {
    return qty * fillPrice;
  }

  if (Number.isFinite(requestedPrice) && requestedPrice > 0) {
    return qty * requestedPrice;
  }

  return null;
};

const PaperTrading = ({ initialTicker = 'AAPL' }) => {
  const theme = useTheme();
  const { user, isAuthenticated, getAccessTokenSilently } = useAuth0();
  const runtimeEnv = window.__RUNTIME_ENV__ || {};
  const authAudience = String(runtimeEnv.AUTH0_AUDIENCE || process.env.REACT_APP_AUTH0_AUDIENCE || '').trim();
  const userId = isAuthenticated && user?.sub ? user.sub : 'anonymous';
  const userEmail = isAuthenticated && user?.email ? String(user.email).trim().toLowerCase() : '';
  const userDisplayName = isAuthenticated && user?.name ? String(user.name).trim() : '';
  const [authToken, setAuthToken] = useState('');

  const getApiAccessToken = useCallback(async ({ preferFresh = false } = {}) => {
    const withAudience = {
      scope: 'openid profile email',
      ...(authAudience ? { audience: authAudience } : {}),
    };

    const tokenOptions = preferFresh ? { cacheMode: 'off' } : {};

    try {
      return String((await getAccessTokenSilently({ authorizationParams: withAudience, ...tokenOptions })) || '');
    } catch (firstError) {
      if (!authAudience) {
        throw firstError;
      }

      // Fallback for environments where audience config is not fully wired.
      const withoutAudience = { scope: 'openid profile email' };
      return String((await getAccessTokenSilently({ authorizationParams: withoutAudience, ...tokenOptions })) || '');
    }
  }, [authAudience, getAccessTokenSilently]);

  useEffect(() => {
    let active = true;

    const loadToken = async () => {
      if (!isAuthenticated) {
        if (active) setAuthToken('');
        return;
      }

      try {
        const token = await getApiAccessToken();
        if (active) {
          setAuthToken(String(token || ''));
        }
      } catch (error) {
        if (active) {
          setAuthToken('');
        }
      }
    };

    loadToken();

    return () => {
      active = false;
    };
  }, [isAuthenticated, getApiAccessToken]);

  const [summary, setSummary] = useState(null);
  const [orders, setOrders] = useState([]);
  const [balanceHistory, setBalanceHistory] = useState([]);
  const [chartMode, setChartMode] = useState('equity');
  const [positionPriceMode, setPositionPriceMode] = useState('normalized');
  const [positionPriceHistory, setPositionPriceHistory] = useState({});
  const [positionPriceLoading, setPositionPriceLoading] = useState(false);
  const [positionPriceError, setPositionPriceError] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [resetStartingCash, setResetStartingCash] = useState('100000');
  const [error, setError] = useState('');
  const [tradeConfirmation, setTradeConfirmation] = useState('');
  const [cryptoOptions, setCryptoOptions] = useState(FALLBACK_CRYPTO_OPTIONS);

  const initialNormalizedTicker = String(initialTicker || 'AAPL').trim().toUpperCase();
  const [assetType, setAssetType] = useState(() => (isCryptoTicker(initialNormalizedTicker) ? 'crypto' : 'stock'));
  const [ticker, setTicker] = useState(initialNormalizedTicker);
  const [side, setSide] = useState('buy');
  const [orderType, setOrderType] = useState('market');
  const [quantity, setQuantity] = useState('1');
  const [limitPrice, setLimitPrice] = useState('');
  const [stopPrice, setStopPrice] = useState('');
  const [trailMode, setTrailMode] = useState('percent');
  const [trailValue, setTrailValue] = useState('');
  const [marketAmountEstimate, setMarketAmountEstimate] = useState(null);
  const [marketAmountLoading, setMarketAmountLoading] = useState(false);
  const [portfolioInsightTab, setPortfolioInsightTab] = useState('alerts');
  const [activityExpanded, setActivityExpanded] = useState(false);
  const [competition, setCompetition] = useState(null);
  const [competitionError, setCompetitionError] = useState('');
  const [competitionLoading, setCompetitionLoading] = useState(false);
  const [selectedCompetitorUserId, setSelectedCompetitorUserId] = useState('');
  const [cancellingOrderId, setCancellingOrderId] = useState(null);
  const [editingOrderId, setEditingOrderId] = useState(null);
  const [bracketTicker, setBracketTicker] = useState('');
  const [bracketQuantity, setBracketQuantity] = useState('');
  const [bracketTakeProfit, setBracketTakeProfit] = useState('');
  const [bracketStopLoss, setBracketStopLoss] = useState('');
  const [bracketSubmitting, setBracketSubmitting] = useState(false);
  const [riskAnalysis, setRiskAnalysis] = useState(null);
  const [riskLoading, setRiskLoading] = useState(false);
  const [riskError, setRiskError] = useState('');
  const [expandedPositionTickers, setExpandedPositionTickers] = useState(() => new Set());

  const buildRequestHeaders = useCallback(async ({ preferFresh = false } = {}) => {
    const headers = {
      'X-User-Id': userId,
      ...(userEmail ? { 'X-User-Email': userEmail } : {}),
      ...(userDisplayName ? { 'X-User-Name': userDisplayName } : {}),
    };
    let token = authToken;

    if (isAuthenticated && (!token || preferFresh)) {
      try {
        token = await getApiAccessToken({ preferFresh });
        if (token) {
          setAuthToken(token);
        }
      } catch (error) {
        token = '';
      }
    }

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    return headers;
  }, [authToken, getApiAccessToken, isAuthenticated, userDisplayName, userEmail, userId]);

  const fetchWithAuthRetry = useCallback(async (url, init = {}) => {
    const runRequest = async (preferFresh) => {
      const requestHeaders = await buildRequestHeaders({ preferFresh });
      return fetch(url, {
        ...init,
        headers: {
          ...requestHeaders,
          ...(init.headers || {}),
        },
      });
    };

    let response = await runRequest(false);
    if (response.status === 401 && isAuthenticated) {
      setAuthToken('');
      response = await runRequest(true);
    }

    return response;
  }, [buildRequestHeaders, isAuthenticated]);

  const loadData = useCallback(async ({ silent = false } = {}) => {
    if (!isAuthenticated) {
      setSummary(null);
      setOrders([]);
      setBalanceHistory([]);
      setError('Please sign in to access paper trading.');
      setLoading(false);
      return;
    }

    if (!silent) {
      setLoading(true);
    }
    setError('');
    try {
      const requestHeaders = await buildRequestHeaders();
      if (!requestHeaders.Authorization) {
        throw new Error('Missing bearer token');
      }

      const competitionQuery = new URLSearchParams();
      if (selectedCompetitorUserId) {
        competitionQuery.set('competitor', selectedCompetitorUserId);
      }
      const competitionUrl = `${API_BASE_URL}/api/paper/competition${competitionQuery.toString() ? `?${competitionQuery.toString()}` : ''}`;

      if (!silent) {
        setCompetitionLoading(true);
      }
      setCompetitionError('');

      const [summaryRes, ordersRes, historyRes, competitionRes] = await Promise.all([
        fetchWithAuthRetry(`${API_BASE_URL}/api/paper/portfolio/summary`, { headers: requestHeaders }),
        fetchWithAuthRetry(`${API_BASE_URL}/api/paper/orders?limit=50`, { headers: requestHeaders }),
        fetchWithAuthRetry(`${API_BASE_URL}/api/paper/balance-history?limit=300`, { headers: requestHeaders }),
        fetchWithAuthRetry(competitionUrl, { headers: requestHeaders }),
      ]);

      if (!summaryRes.ok) {
        throw new Error(`Summary request failed (${summaryRes.status})`);
      }
      if (!ordersRes.ok) {
        throw new Error(`Orders request failed (${ordersRes.status})`);
      }
      if (!historyRes.ok) {
        throw new Error(`Balance history request failed (${historyRes.status})`);
      }

      const summaryPayload = await summaryRes.json();
      const ordersPayload = await ordersRes.json();
      const historyPayload = await historyRes.json();

      setSummary(summaryPayload);
      setOrders(Array.isArray(ordersPayload) ? ordersPayload : []);
      setBalanceHistory(Array.isArray(historyPayload) ? historyPayload : []);

      if (competitionRes.ok) {
        const competitionPayload = await competitionRes.json();
        setCompetition(competitionPayload || null);

        const competitorId = String(competitionPayload?.competitor?.user_id || '').trim();
        if (competitorId && competitorId !== selectedCompetitorUserId) {
          setSelectedCompetitorUserId(competitorId);
        }
      } else {
        setCompetitionError(`Competition request failed (${competitionRes.status})`);
      }
    } catch (err) {
      setError(err.message || 'Failed to load paper trading data');
    } finally {
      if (!silent) {
        setCompetitionLoading(false);
      }
      if (!silent) {
        setLoading(false);
      }
    }
  }, [buildRequestHeaders, fetchWithAuthRetry, isAuthenticated, selectedCompetitorUserId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    const controller = new AbortController();

    const loadCryptoOptions = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/crypto/markets?limit=100`, {
          signal: controller.signal,
        });
        if (!response.ok) throw new Error(`Crypto catalog request failed (${response.status})`);
        setCryptoOptions(buildCryptoOptions(await response.json()));
      } catch (loadError) {
        if (loadError.name !== 'AbortError') {
          setCryptoOptions(FALLBACK_CRYPTO_OPTIONS);
        }
      }
    };

    loadCryptoOptions();
    return () => controller.abort();
  }, []);

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      loadData({ silent: true });
    }, 60000);

    return () => window.clearInterval(intervalId);
  }, [loadData]);

  useEffect(() => {
    const nextTicker = String(initialTicker || '').trim().toUpperCase();
    if (!nextTicker) {
      return;
    }
    setAssetType(isCryptoTicker(nextTicker) ? 'crypto' : 'stock');
    setTicker((prev) => (prev === nextTicker ? prev : nextTicker));
  }, [initialTicker]);

  const handleAssetTypeChange = (_, nextAssetType) => {
    if (!nextAssetType || nextAssetType === assetType) {
      return;
    }
    setAssetType(nextAssetType);
    setTicker(nextAssetType === 'crypto' ? cryptoOptions[0].symbol : 'AAPL');
    setQuantity(nextAssetType === 'crypto' ? '0.01' : '1');
    setTradeConfirmation('');
    setError('');
  };

  const handleSubmit = async () => {
    setError('');
    setTradeConfirmation('');
    const cleanTicker = String(ticker || '').trim().toUpperCase();
    const cleanQty = Number(quantity);

    if (!/^[A-Z0-9.-]+$/.test(cleanTicker)) {
      setError('Please enter a valid ticker');
      return;
    }
    if (!Number.isFinite(cleanQty) || cleanQty <= 0) {
      setError('Quantity must be a positive number');
      return;
    }
    if (orderType === 'limit') {
      const cleanLimitPrice = Number(limitPrice);
      if (!Number.isFinite(cleanLimitPrice) || cleanLimitPrice <= 0) {
        setError('Limit price must be a positive number');
        return;
      }
    }
    if (orderType === 'stop') {
      const cleanStopPrice = Number(stopPrice);
      if (!Number.isFinite(cleanStopPrice) || cleanStopPrice <= 0) {
        setError('Stop price must be a positive number');
        return;
      }
    }
    if (orderType === 'stop_limit') {
      const cleanStopPrice = Number(stopPrice);
      const cleanLimitPrice = Number(limitPrice);
      if (!Number.isFinite(cleanStopPrice) || cleanStopPrice <= 0) {
        setError('Stop price must be a positive number');
        return;
      }
      if (!Number.isFinite(cleanLimitPrice) || cleanLimitPrice <= 0) {
        setError('Limit price must be a positive number');
        return;
      }
    }
    if (orderType === 'trailing_stop') {
      const cleanTrailValue = Number(trailValue);
      if (!Number.isFinite(cleanTrailValue) || cleanTrailValue <= 0) {
        setError(`Trail ${trailMode === 'percent' ? 'percent' : 'amount'} must be a positive number`);
        return;
      }
    }

    setSubmitting(true);
    try {
      const requestHeaders = await buildRequestHeaders();
      if (!requestHeaders.Authorization) {
        throw new Error('Missing bearer token');
      }

      const response = await fetchWithAuthRetry(`${API_BASE_URL}/api/paper/orders`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...requestHeaders,
        },
        body: JSON.stringify({
          ticker: cleanTicker,
          side,
          orderType,
          quantity: cleanQty,
          limitPrice: (orderType === 'limit' || orderType === 'stop_limit') ? Number(limitPrice) : null,
          stopPrice: (orderType === 'stop' || orderType === 'stop_limit') ? Number(stopPrice) : null,
          trailAmount: orderType === 'trailing_stop' && trailMode === 'amount' ? Number(trailValue) : null,
          trailPercent: orderType === 'trailing_stop' && trailMode === 'percent' ? Number(trailValue) : null,
        }),
      });

      const payload = await response.json();
      if (!response.ok) {
        throw new Error(payload?.error || `Order failed (${response.status})`);
      }

      const order = payload?.order || {};
      const confirmedSide = String(order?.side || side || '').toLowerCase();
      const confirmedQty = Number(order?.quantity);
      const quantityText = Number.isFinite(confirmedQty) && confirmedQty > 0
        ? number(confirmedQty, 6)
        : number(cleanQty, 6);
      const quantityLabel = isCryptoTicker(cleanTicker) ? 'units' : 'shares';
      const amountValue = calculateOrderTotal(order);
      const amountText = Number.isFinite(amountValue)
        ? currency(amountValue)
        : (orderType === 'limit' && Number.isFinite(cleanQty) && Number.isFinite(Number(limitPrice))
          ? currency(cleanQty * Number(limitPrice))
          : 'Pending fill');
      const actionText = confirmedSide === 'sell' ? 'Sell' : 'Buy';
      const orderStatus = String(order?.status || '').toLowerCase();

      if (orderStatus === 'pending') {
        const conditionText = orderType === 'stop'
          ? `stop @ ${currency(Number(stopPrice))}`
          : orderType === 'stop_limit'
            ? `stop @ ${currency(Number(stopPrice))} / limit @ ${currency(Number(limitPrice))}`
            : orderType === 'trailing_stop'
              ? `trailing ${trailMode === 'percent' ? `${trailValue}%` : currency(Number(trailValue))}`
              : `limit @ ${currency(Number(limitPrice))}`;
        setTradeConfirmation(`${actionText} order working: ${quantityText} ${quantityLabel} (${conditionText}).`);
      } else {
        const feesValue = (Number(order?.commission) || 0) + (Number(order?.regulatory_fees) || 0);
        const feesText = feesValue > 0 ? ` (fees ${currency(feesValue)})` : '';
        setTradeConfirmation(
          `${actionText} confirmed: ${quantityText} ${quantityLabel} ${confirmedSide === 'sell' ? 'sold' : 'bought'} at ${currency(Number(order?.filled_price))}, total ${amountText}${feesText}.`
        );
      }

      setQuantity(isCryptoTicker(cleanTicker) ? '0.01' : '1');
      if (orderType === 'limit' || orderType === 'stop_limit') {
        setLimitPrice('');
      }
      if (orderType === 'stop' || orderType === 'stop_limit') {
        setStopPrice('');
      }
      if (orderType === 'trailing_stop') {
        setTrailValue('');
      }
      await loadData();
    } catch (err) {
      setError(err.message || 'Failed to submit order');
    } finally {
      setSubmitting(false);
    }
  };

  const handlePlaceOcoBracket = async () => {
    setError('');
    setTradeConfirmation('');
    const cleanTicker = String(bracketTicker || '').trim().toUpperCase();
    const qty = Number(bracketQuantity);
    const takeProfit = Number(bracketTakeProfit);
    const stopLoss = Number(bracketStopLoss);

    if (!/^[A-Z0-9.-]+$/.test(cleanTicker)) {
      setError('Select a ticker for the protective bracket');
      return;
    }
    if (!Number.isFinite(qty) || qty <= 0) {
      setError('Bracket quantity must be a positive number');
      return;
    }
    if (!Number.isFinite(takeProfit) || takeProfit <= 0 || !Number.isFinite(stopLoss) || stopLoss <= 0) {
      setError('Take-profit and stop-loss prices must be positive numbers');
      return;
    }
    if (takeProfit <= stopLoss) {
      setError('Take-profit price must be greater than stop-loss price');
      return;
    }

    setBracketSubmitting(true);
    try {
      const requestHeaders = await buildRequestHeaders();
      if (!requestHeaders.Authorization) {
        throw new Error('Missing bearer token');
      }

      const response = await fetchWithAuthRetry(`${API_BASE_URL}/api/paper/orders/oco`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...requestHeaders,
        },
        body: JSON.stringify({
          ticker: cleanTicker,
          side: 'sell',
          quantity: qty,
          takeProfitPrice: takeProfit,
          stopLossPrice: stopLoss,
        }),
      });

      const payload = await response.json();
      if (!response.ok) {
        throw new Error(payload?.error || `Bracket order failed (${response.status})`);
      }

      setTradeConfirmation(
        `Protective bracket placed for ${cleanTicker}: take-profit @ ${currency(takeProfit)} / stop-loss @ ${currency(stopLoss)}.`
      );
      setBracketQuantity('');
      setBracketTakeProfit('');
      setBracketStopLoss('');
      await loadData();
    } catch (err) {
      setError(err.message || 'Failed to place protective bracket');
    } finally {
      setBracketSubmitting(false);
    }
  };

  const handleResetAccount = async (startingCash) => {
    setError('');

    setResetting(true);
    try {
      const requestHeaders = await buildRequestHeaders();
      if (!requestHeaders.Authorization) {
        throw new Error('Missing bearer token');
      }

      const parsedStartingCash = Number(startingCash);
      const response = await fetchWithAuthRetry(`${API_BASE_URL}/api/paper/account/reset`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...requestHeaders,
        },
        body: JSON.stringify(
          Number.isFinite(parsedStartingCash) && parsedStartingCash > 0
            ? { startingCash: parsedStartingCash }
            : {}
        ),
      });
      const payload = await response.json();
      if (!response.ok) {
        throw new Error(payload?.error || `Reset failed (${response.status})`);
      }
      await loadData();
    } catch (err) {
      setError(err.message || 'Failed to reset account');
    } finally {
      setResetting(false);
    }
  };

  const handleOpenResetDialog = () => {
    setResetStartingCash('100000');
    setResetDialogOpen(true);
  };

  const handleConfirmResetAccount = async () => {
    setResetDialogOpen(false);
    await handleResetAccount(resetStartingCash);
  };

  const handleCancelPendingOrder = async (orderId) => {
    const parsedOrderId = Number(orderId);
    if (!Number.isInteger(parsedOrderId) || parsedOrderId <= 0) {
      setError('Invalid order selected for cancellation');
      return;
    }

    const confirmed = window.confirm('Cancel this pending limit order?');
    if (!confirmed) {
      return;
    }

    setError('');
    setTradeConfirmation('');
    setCancellingOrderId(parsedOrderId);
    try {
      const requestHeaders = await buildRequestHeaders();
      if (!requestHeaders.Authorization) {
        throw new Error('Missing bearer token');
      }

      const response = await fetchWithAuthRetry(`${API_BASE_URL}/api/paper/orders/${parsedOrderId}`, {
        method: 'DELETE',
        headers: {
          ...requestHeaders,
        },
      });

      const payload = await response.json();
      if (!response.ok) {
        throw new Error(payload?.error || `Cancellation failed (${response.status})`);
      }

      const cancelledTicker = String(payload?.order?.ticker || '').trim().toUpperCase();
      setTradeConfirmation(cancelledTicker
        ? `Pending order cancelled for ${cancelledTicker}.`
        : 'Pending order cancelled.');
      await loadData({ silent: true });
    } catch (err) {
      setError(err.message || 'Failed to cancel order');
    } finally {
      setCancellingOrderId(null);
    }
  };

  const handleEditPendingOrder = async (order) => {
    const parsedOrderId = Number(order?.id);
    if (!Number.isInteger(parsedOrderId) || parsedOrderId <= 0) {
      setError('Invalid order selected for edit');
      return;
    }

    const orderKind = String(order?.order_type || 'limit').toLowerCase();
    if (orderKind === 'trailing_stop') {
      setError('Trailing-stop orders cannot be edited; cancel and re-place instead.');
      return;
    }

    const currentQty = Number(order?.quantity);
    const currentLimit = Number(order?.requested_price);
    const currentStop = Number(order?.stop_price);

    const qtyInput = window.prompt(
      `Update ${isCryptoTicker(order?.ticker) ? 'units' : 'shares'} quantity`,
      Number.isFinite(currentQty) ? String(currentQty) : ''
    );
    if (qtyInput === null) {
      return;
    }

    let nextLimitPrice = null;
    let nextStopPrice = null;

    if (orderKind === 'limit' || orderKind === 'stop_limit') {
      const limitInput = window.prompt('Update limit price', Number.isFinite(currentLimit) ? String(currentLimit) : '');
      if (limitInput === null) {
        return;
      }
      nextLimitPrice = Number(limitInput);
      if (!Number.isFinite(nextLimitPrice) || nextLimitPrice <= 0) {
        setError('Limit price must be a positive number');
        return;
      }
    }

    if (orderKind === 'stop' || orderKind === 'stop_limit') {
      const stopInput = window.prompt('Update stop price', Number.isFinite(currentStop) ? String(currentStop) : '');
      if (stopInput === null) {
        return;
      }
      nextStopPrice = Number(stopInput);
      if (!Number.isFinite(nextStopPrice) || nextStopPrice <= 0) {
        setError('Stop price must be a positive number');
        return;
      }
    }

    const nextQuantity = Number(qtyInput);

    if (!Number.isFinite(nextQuantity) || nextQuantity <= 0) {
      setError('Quantity must be a positive number');
      return;
    }

    setError('');
    setTradeConfirmation('');
    setEditingOrderId(parsedOrderId);
    try {
      const requestHeaders = await buildRequestHeaders();
      if (!requestHeaders.Authorization) {
        throw new Error('Missing bearer token');
      }

      const response = await fetchWithAuthRetry(`${API_BASE_URL}/api/paper/orders/${parsedOrderId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          ...requestHeaders,
        },
        body: JSON.stringify({
          quantity: nextQuantity,
          ...(nextLimitPrice != null ? { limitPrice: nextLimitPrice } : {}),
          ...(nextStopPrice != null ? { stopPrice: nextStopPrice } : {}),
        }),
      });

      const payload = await response.json();
      if (!response.ok) {
        throw new Error(payload?.error || `Edit failed (${response.status})`);
      }

      const updatedTicker = String(payload?.order?.ticker || '').trim().toUpperCase();
      const updatedStatus = String(payload?.order?.status || '').trim().toLowerCase();
      if (updatedStatus === 'filled') {
        setTradeConfirmation(updatedTicker
          ? `Order updated and filled for ${updatedTicker}.`
          : 'Order updated and filled.');
      } else {
        setTradeConfirmation(updatedTicker
          ? `Pending order updated for ${updatedTicker}.`
          : 'Pending order updated.');
      }

      await loadData({ silent: true });
    } catch (err) {
      setError(err.message || 'Failed to edit order');
    } finally {
      setEditingOrderId(null);
    }
  };

  const metrics = summary?.metrics || {};
  const account = summary?.account || {};
  const positions = useMemo(() => (Array.isArray(summary?.positions) ? summary.positions : []), [summary]);
  const positionTickers = useMemo(
    () => positions.map((position) => String(position?.ticker || '').trim().toUpperCase()).filter(Boolean),
    [positions]
  );
  const positionOpenedAtByTicker = useMemo(() => Object.fromEntries(
    positions.map((position) => [
      String(position?.ticker || '').trim().toUpperCase(),
      String(position?.created_at || ''),
    ])
  ), [positions]);

  useEffect(() => {
    if (!positionTickers.length) {
      setPositionPriceHistory({});
      setPositionPriceError('');
      setPositionPriceLoading(false);
      return undefined;
    }

    const controller = new AbortController();
    const loadPositionPrices = async () => {
      setPositionPriceLoading(true);
      setPositionPriceError('');

      const results = await Promise.allSettled(positionTickers.map(async (positionTicker) => {
        const openedTimestamp = Date.parse(positionOpenedAtByTicker[positionTicker]);
        const ageYears = Number.isFinite(openedTimestamp)
          ? Math.ceil((Date.now() - openedTimestamp) / (365.25 * 24 * 60 * 60 * 1000))
          : 1;
        const historyYears = Math.min(Math.max(ageYears, 1), 30);
        if (isCryptoTicker(positionTicker)) {
          const cryptoOption = cryptoOptions.find((option) => option.symbol === positionTicker);
          if (!cryptoOption?.id) throw new Error(`${positionTicker} history unavailable`);
          const historyDays = Math.min(historyYears * 366, 3650);
          const cryptoResponse = await fetch(
            `${API_BASE_URL}/api/crypto/market-history/${encodeURIComponent(cryptoOption.id)}?days=${historyDays}&symbol=${encodeURIComponent(cryptoOption.baseSymbol)}`,
            { signal: controller.signal }
          );
          if (!cryptoResponse.ok) throw new Error(`${positionTicker} history unavailable`);
          const payload = await cryptoResponse.json();
          const points = (Array.isArray(payload?.prices) ? payload.prices : [])
            .map(([timestamp, value]) => ({ timestamp: Number(timestamp), value: Number(value) }))
            .filter((point) => Number.isFinite(point.timestamp) && point.value > 0)
            .map((point) => ({ date: new Date(point.timestamp).toISOString().slice(0, 10), value: point.value }));
          if (!points.length) throw new Error(`${positionTicker} history unavailable`);
          return [positionTicker, points];
        }

        const stockResponse = await fetch(
          `${API_BASE_URL}/api/stock-price-history/${encodeURIComponent(positionTicker)}?years=${historyYears}`,
          { signal: controller.signal }
        );
        if (!stockResponse.ok) throw new Error(`${positionTicker} history unavailable`);
        const payload = await stockResponse.json();
        const points = (Array.isArray(payload?.points) ? payload.points : [])
          .filter((point) => Number(point?.value) > 0);
        if (!points.length) throw new Error(`${positionTicker} history unavailable`);
        return [positionTicker, points];
      }));

      if (controller.signal.aborted) return;
      const loadedEntries = results
        .filter((result) => result.status === 'fulfilled')
        .map((result) => result.value);
      setPositionPriceHistory(Object.fromEntries(loadedEntries));
      const failedCount = results.length - loadedEntries.length;
      if (failedCount) {
        setPositionPriceError(`${failedCount} position${failedCount === 1 ? '' : 's'} could not load price history.`);
      }
      setPositionPriceLoading(false);
    };

    loadPositionPrices().catch((loadError) => {
      if (loadError.name === 'AbortError') return;
      setPositionPriceHistory({});
      setPositionPriceError(loadError.message || 'Unable to load position price history.');
      setPositionPriceLoading(false);
    });

    return () => controller.abort();
  }, [cryptoOptions, positionOpenedAtByTicker, positionTickers]);

  const positionPriceChartData = useMemo(
    () => buildPositionPriceChartData(positionPriceHistory, positionTickers, positionPriceMode, positionOpenedAtByTicker),
    [positionOpenedAtByTicker, positionPriceHistory, positionPriceMode, positionTickers]
  );
  const cashBalance = Number(account.cash_balance);
  const totalEquity = Number(metrics.equity);
  const totalMarketValue = Number.isFinite(Number(metrics.market_value))
    ? Number(metrics.market_value)
    : (Number.isFinite(totalEquity) && Number.isFinite(cashBalance) ? totalEquity - cashBalance : NaN);
  const chartData = useMemo(
    () => {
      const basePoints = balanceHistory.map((point) => ({
        ...point,
        label: formatDateTimeInAppTimeZone(point.created_at),
        balance: Number(point.balance),
        equity: Number.isFinite(Number(point.equity)) ? Number(point.equity) : Number(point.balance),
        market_value:
          (Number.isFinite(Number(point.equity)) ? Number(point.equity) : Number(point.balance)) - Number(point.balance),
      }));

      // Keep chart performance aligned with the live summary cards by appending a current snapshot.
      if (Number.isFinite(totalEquity) && Number.isFinite(cashBalance)) {
        basePoints.push({
          created_at: new Date().toISOString(),
          label: formatDateTimeInAppTimeZone(new Date().toISOString()),
          balance: cashBalance,
          equity: totalEquity,
          market_value: Number.isFinite(totalMarketValue) ? totalMarketValue : (totalEquity - cashBalance),
        });
      }

      return basePoints;
    },
    [balanceHistory, cashBalance, totalEquity, totalMarketValue]
  );

  const chartMetricConfig = {
    equity: { key: 'equity', label: 'Total Equity', color: '#2563eb' },
    market_value: { key: 'market_value', label: 'Market Value', color: '#7c3aed' },
    balance: { key: 'balance', label: 'Cash Balance', color: '#0f766e' },
  };

  const selectedMetric = chartMetricConfig[chartMode] || chartMetricConfig.equity;

  const cleanTicker = useMemo(() => String(ticker || '').trim().toUpperCase() || 'AAPL', [ticker]);
  const selectedCryptoOption = useMemo(
    () => cryptoOptions.find((option) => option.symbol === cleanTicker) || null,
    [cleanTicker, cryptoOptions]
  );
  const useNativeCryptoChart = assetType === 'crypto' && selectedCryptoOption && !selectedCryptoOption.hasTradingView;
  const tradingViewSymbol = useMemo(
    () => selectedCryptoOption?.tradingViewSymbol || cleanTicker,
    [cleanTicker, selectedCryptoOption]
  );
  const [chartInterval, setChartInterval] = useState('D');
  const [chartStyle, setChartStyle] = useState('1');
  const [chartStudies, setChartStudies] = useState([CHART_VOLUME_OPTION]);
  const tradingViewUrl = useMemo(() => {
    const params = new URLSearchParams({
      symbol: tradingViewSymbol,
      interval: chartInterval,
      hidetoptoolbar: '0',
      symboledit: '1',
      saveimage: '0',
      toolbarbg: 'f1f3f6',
      theme: 'dark',
      style: chartStyle,
      timezone: 'America/New_York',
      withdateranges: '1',
      hide_side_toolbar: '0',
      allow_symbol_change: '1',
      hideideas: '1',
      hidevolume: chartStudies.includes(CHART_VOLUME_OPTION) ? '0' : '1',
    });
    chartStudies
      .filter((study) => study !== CHART_VOLUME_OPTION)
      .forEach((study) => params.append('studies', study));
    return `https://s.tradingview.com/widgetembed/?${params.toString()}`;
  }, [tradingViewSymbol, chartInterval, chartStyle, chartStudies]);

  const quantityNum = Number(quantity);
  const limitPriceNum = Number(limitPrice);
  const estimatedNotional =
    orderType === 'limit' && Number.isFinite(quantityNum) && quantityNum > 0 && Number.isFinite(limitPriceNum) && limitPriceNum > 0
      ? quantityNum * limitPriceNum
      : null;

  useEffect(() => {
    let active = true;
    const cleanTicker = String(ticker || '').trim().toUpperCase();
    const qty = Number(quantity);

    if (orderType === 'limit' || !/^[A-Z0-9.-]+$/.test(cleanTicker) || !Number.isFinite(qty) || qty <= 0) {
      setMarketAmountEstimate(null);
      setMarketAmountLoading(false);
      return () => {
        active = false;
      };
    }

    const estimateMarketAmount = async () => {
      setMarketAmountLoading(true);
      try {
        const response = await fetch(`${API_BASE_URL}/api/stock-price/${encodeURIComponent(cleanTicker)}`);
        if (!response.ok) {
          throw new Error(`Quote request failed (${response.status})`);
        }

        const payload = await response.json();
        const priceCandidates = [
          Number(payload?.price),
          Number(payload?.currentPrice),
          Number(payload?.regularMarketPrice),
        ];
        const quotePrice = priceCandidates.find((candidate) => Number.isFinite(candidate) && candidate > 0);

        if (!active) return;

        if (Number.isFinite(quotePrice)) {
          setMarketAmountEstimate(qty * quotePrice);
        } else {
          setMarketAmountEstimate(null);
        }
      } catch (_error) {
        if (active) {
          setMarketAmountEstimate(null);
        }
      } finally {
        if (active) {
          setMarketAmountLoading(false);
        }
      }
    };

    estimateMarketAmount();

    return () => {
      active = false;
    };
  }, [orderType, quantity, ticker]);

  const estimatedTransactionAmount = orderType === 'limit' ? estimatedNotional : marketAmountEstimate;
  const amountFieldValue = marketAmountLoading
    ? 'Loading...'
    : (Number.isFinite(estimatedTransactionAmount) ? currency(estimatedTransactionAmount) : '-');

  const positionTotals = useMemo(() => positions.reduce((acc, position) => {
    acc.quantity += Number(position.quantity) || 0;
    acc.marketValue += Number(position.market_value) || 0;
    acc.unrealizedPnl += Number(position.unrealized_pnl) || 0;
    acc.realizedPnl += Number(position.realized_pnl) || 0;
    return acc;
  }, {
    quantity: 0,
    marketValue: 0,
    unrealizedPnl: 0,
    realizedPnl: 0,
  }), [positions]);

  const pendingLimitOrders = useMemo(
    () => orders.filter((order) => order.status === 'pending' && PAPER_PENDING_ORDER_TYPES.includes(String(order.order_type || '').toLowerCase())),
    [orders]
  );

  const orderConditionLabel = (order) => {
    const kind = String(order?.order_type || '').toLowerCase();
    if (kind === 'stop') {
      return `Stop @ ${currency(Number(order.stop_price))}`;
    }
    if (kind === 'stop_limit') {
      return `Stop ${currency(Number(order.stop_price))} / Lmt ${currency(Number(order.requested_price))}`;
    }
    if (kind === 'trailing_stop') {
      const trailText = Number.isFinite(Number(order.trail_percent))
        ? `${Number(order.trail_percent)}%`
        : currency(Number(order.trail_amount));
      return `Trail ${trailText}${Number.isFinite(Number(order.stop_price)) ? ` (now ${currency(Number(order.stop_price))})` : ''}`;
    }
    return currency(Number(order.requested_price));
  };

  const handleChartModeChange = (_, nextMode) => {
    if (!nextMode) {
      return;
    }
    setChartMode(nextMode);
  };

  const chartAxisColor = theme.palette.text.secondary;
  const chartGridColor = theme.palette.divider;
  const chartTooltipBg = theme.palette.mode === 'dark' ? 'rgba(17, 24, 39, 0.96)' : '#ffffff';

  const loadRiskAnalysis = useCallback(async () => {
    if (!isAuthenticated) {
      return;
    }
    setRiskLoading(true);
    setRiskError('');
    try {
      const requestHeaders = await buildRequestHeaders();
      if (!requestHeaders.Authorization) {
        throw new Error('Missing bearer token');
      }
      const response = await fetchWithAuthRetry(`${API_BASE_URL}/api/paper/risk-analysis`, { headers: requestHeaders });
      if (!response.ok) {
        throw new Error(`Risk analysis request failed (${response.status})`);
      }
      const payload = await response.json();
      setRiskAnalysis(payload);
    } catch (err) {
      setRiskError(err.message || 'Failed to load risk analysis');
    } finally {
      setRiskLoading(false);
    }
  }, [buildRequestHeaders, fetchWithAuthRetry, isAuthenticated]);

  const handleInsightTabChange = (_, nextTab) => {
    if (!nextTab) {
      return;
    }
    setPortfolioInsightTab(nextTab);
    if (nextTab === 'risk' && !riskAnalysis && !riskLoading) {
      loadRiskAnalysis();
    }
  };

  const togglePositionRiskRow = (positionTicker) => {
    setExpandedPositionTickers((prev) => {
      const next = new Set(prev);
      if (next.has(positionTicker)) {
        next.delete(positionTicker);
      } else {
        next.add(positionTicker);
        if (!riskAnalysis && !riskLoading) {
          loadRiskAnalysis();
        }
      }
      return next;
    });
  };

  // Fewer buckets than the portfolio-level SPY risk graph to keep the per-position chart readable.
  const POSITION_RISK_MOVES_PCT = [-100, -50, -25, -10, -5, 0, 5, 10, 25, 50, 100];

  // Per-position projected P/L across hypothetical moves of the position's own stock (not SPY),
  // plus a real bar for where the position currently sits.
  const getPositionRiskGraph = (position) => {
    const positionRisk = riskAnalysis?.positions?.find((p) => p.ticker === position.ticker) || null;
    const marketValue = Number(position.market_value) || 0;
    const costBasis = Math.abs(Number(position.avg_cost)) * Math.abs(Number(position.quantity));
    const currentReturnPct = costBasis > 0 ? (Number(position.unrealized_pnl) / costBasis) * 100 : null;

    const graph = POSITION_RISK_MOVES_PCT.map((movePct) => ({
      move_pct: movePct,
      projected_pnl: marketValue * (movePct / 100),
      isCurrent: false,
    }));

    if (currentReturnPct !== null) {
      graph.push({
        move_pct: Number(currentReturnPct.toFixed(1)),
        projected_pnl: Number(position.unrealized_pnl) || 0,
        isCurrent: true,
      });
      graph.sort((a, b) => a.move_pct - b.move_pct);
    }

    return { positionRisk, graph, currentReturnPct };
  };

  const competitorEntry = competition?.competitor || null;
  const followingEntries = Array.isArray(competition?.following) ? competition.following : [];
  const competitionLeaderboard = Array.isArray(competition?.leaderboard) ? competition.leaderboard : [];
  const myCompetitionSummary = competition?.me?.summary || summary;
  const competitorSummary = competitorEntry?.summary || null;

  const myReturnPct = useMemo(() => calculateReturnPct(myCompetitionSummary), [myCompetitionSummary]);
  const competitorReturnPct = useMemo(() => calculateReturnPct(competitorSummary), [competitorSummary]);

  const competitionChartData = useMemo(() => {
    const myHistory = Array.isArray(competition?.me?.history) ? competition.me.history : [];
    const otherHistory = Array.isArray(competitorEntry?.history) ? competitorEntry.history : [];

    if (!myHistory.length || !otherHistory.length) {
      return [];
    }

    const rows = new Map();
    for (const point of myHistory) {
      const key = String(point?.created_at || '');
      if (!key) continue;
      const existing = rows.get(key) || { ts: key, me: null, competitor: null };
      existing.me = Number.isFinite(Number(point?.equity)) ? Number(point.equity) : null;
      rows.set(key, existing);
    }
    for (const point of otherHistory) {
      const key = String(point?.created_at || '');
      if (!key) continue;
      const existing = rows.get(key) || { ts: key, me: null, competitor: null };
      existing.competitor = Number.isFinite(Number(point?.equity)) ? Number(point.equity) : null;
      rows.set(key, existing);
    }

    return Array.from(rows.values())
      .sort((a, b) => new Date(a.ts).getTime() - new Date(b.ts).getTime())
      .map((row) => ({
        ...row,
        label: formatDateTimeInAppTimeZone(row.ts),
      }));
  }, [competition, competitorEntry]);

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={700}>Portfolio Builder</Typography>
          <Typography variant="body2" color="text.secondary">
            Practice long-term position sizing and rebalancing with virtual capital.
          </Typography>
        </Box>
        <Button variant="outlined" color="error" onClick={handleOpenResetDialog} disabled={resetting || loading}>
          {resetting ? 'Resetting...' : 'Reset Portfolio'}
        </Button>
      </Stack>

      <Dialog open={resetDialogOpen} onClose={() => setResetDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Reset Portfolio</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            This clears all positions and order history. Choose a new starting cash balance for the reset account.
          </DialogContentText>
          <TextField
            autoFocus
            fullWidth
            label="Starting Cash ($)"
            type="number"
            value={resetStartingCash}
            onChange={(e) => setResetStartingCash(e.target.value)}
            inputProps={{ min: 0, step: '1000' }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmResetAccount}
            disabled={!Number.isFinite(Number(resetStartingCash)) || Number(resetStartingCash) <= 0}
          >
            Reset
          </Button>
        </DialogActions>
      </Dialog>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {tradeConfirmation && <Alert severity="success" sx={{ mb: 2 }}>{tradeConfirmation}</Alert>}
      {Boolean(metrics.margin_call) && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Margin call: equity ({currency(Number(metrics.equity))}) is below the maintenance requirement
          ({currency(Number(metrics.maintenance_requirement))}). Only risk-reducing trades (closing longs, covering shorts) are allowed until this clears.
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Paper sx={{ p: 2, mb: 2 }}>
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              justifyContent="space-between"
              alignItems={{ xs: 'flex-start', md: 'center' }}
              spacing={1.5}
              sx={{ mb: 1.5 }}
            >
              <Typography variant="h6">Chart — {cleanTicker}</Typography>
              {!useNativeCryptoChart && (
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', sm: 'center' }} flexWrap="wrap">
                <ToggleButtonGroup
                  value={chartInterval}
                  exclusive
                  size="small"
                  onChange={(_, next) => next && setChartInterval(next)}
                  aria-label="chart interval"
                >
                  {CHART_INTERVAL_OPTIONS.map((option) => (
                    <ToggleButton key={option.value} value={option.value}>{option.label}</ToggleButton>
                  ))}
                </ToggleButtonGroup>
                <Select size="small" value={chartStyle} onChange={(e) => setChartStyle(e.target.value)} sx={{ minWidth: 150 }}>
                  {CHART_STYLE_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                  ))}
                </Select>
                <Select
                  size="small"
                  multiple
                  displayEmpty
                  value={chartStudies}
                  onChange={(e) => setChartStudies(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
                  renderValue={(selected) => (selected.length === 0 ? 'Indicators' : `Indicators (${selected.length})`)}
                  sx={{ minWidth: 160 }}
                >
                  {CHART_STUDY_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      <Checkbox size="small" checked={chartStudies.includes(option.value)} />
                      <ListItemText primary={option.label} />
                    </MenuItem>
                  ))}
                </Select>
                </Stack>
              )}
            </Stack>
            <Box sx={{ borderRadius: 1.5, overflow: 'hidden', border: '1px solid', borderColor: 'divider', height: 520, p: useNativeCryptoChart ? 1.5 : 0 }}>
              {useNativeCryptoChart ? (
                <CryptoPriceChart
                  coin={{
                    id: selectedCryptoOption.id,
                    symbol: selectedCryptoOption.baseSymbol,
                    name: selectedCryptoOption.name,
                  }}
                  isDarkMode={theme.palette.mode === 'dark'}
                  height="100%"
                />
              ) : (
                <iframe
                  key={tradingViewSymbol}
                  title={`TradingView ${tradingViewSymbol}`}
                  src={tradingViewUrl}
                  style={{ width: '100%', height: '100%', border: 'none' }}
                  allow="clipboard-write"
                />
              )}
            </Box>
          </Paper>

          <Paper sx={{ p: 2, mb: 2 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>Position Update</Typography>
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={1.5}
              alignItems={{ xs: 'stretch', md: 'center' }}
              flexWrap="wrap"
            >
              <ToggleButtonGroup
                value={assetType}
                exclusive
                size="small"
                onChange={handleAssetTypeChange}
                aria-label="asset type"
              >
                <ToggleButton value="stock">Stocks</ToggleButton>
                <ToggleButton value="crypto">Crypto</ToggleButton>
              </ToggleButtonGroup>
              {assetType === 'crypto' ? (
                <Autocomplete
                  disableClearable
                  options={cryptoOptions}
                  value={cryptoOptions.find((option) => option.symbol === ticker) || cryptoOptions[0]}
                  onChange={(_, option) => {
                    setTicker(option.symbol);
                    setTradeConfirmation('');
                  }}
                  sx={{ minWidth: 190 }}
                  getOptionLabel={(option) => `${option.name} (${option.symbol.replace('-USD', '')})`}
                  isOptionEqualToValue={(option, value) => option.symbol === value.symbol}
                  renderInput={(params) => <TextField {...params} aria-label="Cryptocurrency" />}
                />
              ) : (
                <TextField
                  label="Ticker"
                  value={ticker}
                  onChange={(e) => {
                    setTicker(String(e.target.value || '').toUpperCase().replace(/[^A-Z0-9.-]/g, ''));
                    setTradeConfirmation('');
                  }}
                  sx={{ minWidth: 140 }}
                />
              )}
              <Select value={side} onChange={(e) => setSide(e.target.value)} sx={{ minWidth: 140 }}>
                <MenuItem value="buy">Buy</MenuItem>
                <MenuItem value="sell">Sell</MenuItem>
              </Select>
              <Select value={orderType} onChange={(e) => setOrderType(e.target.value)} sx={{ minWidth: 150 }}>
                <MenuItem value="market">Market</MenuItem>
                <MenuItem value="limit">Limit (LMT)</MenuItem>
                <MenuItem value="stop">Stop</MenuItem>
                <MenuItem value="stop_limit">Stop-Limit</MenuItem>
                <MenuItem value="trailing_stop">Trailing Stop</MenuItem>
              </Select>
              {(orderType === 'limit' || orderType === 'stop_limit') && (
                <TextField
                  label="Limit Price"
                  type="number"
                  value={limitPrice}
                  onChange={(e) => setLimitPrice(e.target.value)}
                  inputProps={{ min: 0, step: '0.0001' }}
                  sx={{ minWidth: 170 }}
                />
              )}
              {(orderType === 'stop' || orderType === 'stop_limit') && (
                <TextField
                  label="Stop Price"
                  type="number"
                  value={stopPrice}
                  onChange={(e) => setStopPrice(e.target.value)}
                  inputProps={{ min: 0, step: '0.0001' }}
                  sx={{ minWidth: 170 }}
                />
              )}
              {orderType === 'trailing_stop' && (
                <>
                  <Select value={trailMode} onChange={(e) => setTrailMode(e.target.value)} sx={{ minWidth: 130 }}>
                    <MenuItem value="percent">Trail %</MenuItem>
                    <MenuItem value="amount">Trail $</MenuItem>
                  </Select>
                  <TextField
                    label={trailMode === 'percent' ? 'Trail Percent' : 'Trail Amount'}
                    type="number"
                    value={trailValue}
                    onChange={(e) => setTrailValue(e.target.value)}
                    inputProps={{ min: 0, step: '0.01' }}
                    sx={{ minWidth: 150 }}
                  />
                </>
              )}
              <TextField
                label={assetType === 'crypto' ? 'Units' : 'Shares'}
                type="number"
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                inputProps={{ min: 0, step: assetType === 'crypto' ? '0.000001' : '1' }}
                sx={{ minWidth: 160 }}
              />
              <TextField
                label={orderType === 'limit' ? 'Amount' : 'Amount (Est.)'}
                value={amountFieldValue}
                InputProps={{ readOnly: true }}
                sx={{ minWidth: 170 }}
              />
              <Button variant="contained" onClick={handleSubmit} disabled={submitting}>
                {submitting ? 'Saving...' : `${side === 'buy' ? 'Buy' : 'Sell'} Position`}
              </Button>
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1.25 }}>
              {orderType === 'limit'
                ? `${side === 'buy' ? 'Estimated Buy Amount' : 'Estimated Sell Amount'}: ${currency(estimatedNotional)}`
                : orderType === 'market'
                  ? 'Amount (Est.) uses the latest quote; final fill may vary slightly.'
                  : 'This is a contingent order — it will sit as Pending until the trigger condition is met, then fill at the market price.'}
            </Typography>
            {side === 'sell' && (() => {
              const heldQty = positions.find((p) => p.ticker === String(ticker || '').trim().toUpperCase());
              const availableQty = heldQty ? Math.max(0, Number(heldQty.quantity)) : 0;
              const willShort = Number(quantity) > availableQty;
              return willShort ? (
                <Typography variant="body2" color="warning.main" sx={{ mt: 0.5 }}>
                  This sell will {availableQty > 0 ? 'close your long position and ' : ''}open or increase a short position, using margin buying power.
                </Typography>
              ) : null;
            })()}
          </Paper>

          <Paper sx={{ p: 2, mb: 2 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>Protective Bracket (OCO)</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Attach a take-profit limit and a stop-loss stop to an existing position. Whichever leg fills first automatically cancels the other.
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', sm: 'center' }} flexWrap="wrap">
              <Select
                value={bracketTicker}
                onChange={(e) => setBracketTicker(e.target.value)}
                displayEmpty
                sx={{ minWidth: 160 }}
              >
                <MenuItem value="" disabled>Select position</MenuItem>
                {positions.map((position) => (
                  <MenuItem key={position.ticker} value={position.ticker}>{position.ticker}</MenuItem>
                ))}
              </Select>
              <TextField
                label={isCryptoTicker(bracketTicker) ? 'Units' : 'Shares'}
                type="number"
                value={bracketQuantity}
                onChange={(e) => setBracketQuantity(e.target.value)}
                inputProps={{ min: 0, step: isCryptoTicker(bracketTicker) ? '0.000001' : '1' }}
                sx={{ minWidth: 130 }}
              />
              <TextField
                label="Take-Profit Price"
                type="number"
                value={bracketTakeProfit}
                onChange={(e) => setBracketTakeProfit(e.target.value)}
                inputProps={{ min: 0, step: '0.0001' }}
                sx={{ minWidth: 170 }}
              />
              <TextField
                label="Stop-Loss Price"
                type="number"
                value={bracketStopLoss}
                onChange={(e) => setBracketStopLoss(e.target.value)}
                inputProps={{ min: 0, step: '0.0001' }}
                sx={{ minWidth: 170 }}
              />
              <Button variant="outlined" onClick={handlePlaceOcoBracket} disabled={bracketSubmitting || positions.length === 0}>
                {bracketSubmitting ? 'Saving...' : 'Place Bracket'}
              </Button>
            </Stack>
          </Paper>

          <Paper sx={{ p: 2, mb: 2 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>Open Positions</Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Ticker</TableCell>
                    <TableCell>Side</TableCell>
                    <TableCell align="right">Qty</TableCell>
                    <TableCell align="right">Avg Cost</TableCell>
                    <TableCell align="right">Market Price</TableCell>
                    <TableCell align="right">Market Value</TableCell>
                    <TableCell align="right">Unrealized P/L</TableCell>
                    <TableCell align="right">Realized P/L</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {positions.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={8} align="center">No open positions</TableCell>
                    </TableRow>
                  )}
                  {positions.map((position) => {
                    const isExpanded = expandedPositionTickers.has(position.ticker);
                    const { positionRisk, graph, currentReturnPct } = getPositionRiskGraph(position);
                    return (
                      <React.Fragment key={position.ticker}>
                        <TableRow
                          hover
                          onClick={() => togglePositionRiskRow(position.ticker)}
                          sx={{ cursor: 'pointer' }}
                        >
                          <TableCell sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                            {position.ticker}
                          </TableCell>
                          <TableCell sx={{ color: Number(position.quantity) < 0 ? 'error.main' : 'success.main', fontWeight: 600 }}>
                            {Number(position.quantity) < 0 ? 'SHORT' : 'LONG'}
                          </TableCell>
                          <TableCell align="right">{number(Math.abs(Number(position.quantity)), 6)}</TableCell>
                          <TableCell align="right">{currency(Number(position.avg_cost))}</TableCell>
                          <TableCell align="right">{currency(Number(position.market_price))}</TableCell>
                          <TableCell align="right">{currency(Number(position.market_value))}</TableCell>
                          <TableCell
                            align="right"
                            sx={{ color: Number(position.unrealized_pnl) >= 0 ? 'success.main' : 'error.main' }}
                          >
                            {currency(Number(position.unrealized_pnl))}
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{ color: Number(position.realized_pnl) >= 0 ? 'success.main' : 'error.main' }}
                          >
                            {currency(Number(position.realized_pnl))}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell colSpan={8} sx={{ p: 0, borderBottom: isExpanded ? undefined : 'none' }}>
                            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                              <Box sx={{ px: 2, py: 1.5, bgcolor: 'action.hover' }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                                  {position.ticker} — Market Risk
                                </Typography>
                                {riskLoading ? (
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, py: 1 }}>
                                    <CircularProgress size={18} />
                                    <Typography variant="body2">Loading risk analysis...</Typography>
                                  </Box>
                                ) : riskError ? (
                                  <Alert severity="warning">{riskError}</Alert>
                                ) : !positionRisk ? (
                                  <Typography variant="body2" color="text.secondary">
                                    Risk data unavailable for this position.
                                  </Typography>
                                ) : (
                                  <>
                                    <Grid container spacing={1.5} sx={{ mb: 1.5 }}>
                                      <Grid item xs={12} sm={3}>
                                        <Paper variant="outlined" sx={{ p: 1.25 }}>
                                          <Typography variant="caption" color="text.secondary">Market Sensitivity</Typography>
                                          <Typography variant="h6">{number(Number(positionRisk.beta), 2)}</Typography>
                                        </Paper>
                                      </Grid>
                                      <Grid item xs={12} sm={3}>
                                        <Paper variant="outlined" sx={{ p: 1.25 }}>
                                          <Typography variant="caption" color="text.secondary">Market Value</Typography>
                                          <Typography variant="h6">{currency(Number(positionRisk.market_value))}</Typography>
                                        </Paper>
                                      </Grid>
                                      <Grid item xs={12} sm={3}>
                                        <Paper variant="outlined" sx={{ p: 1.25 }}>
                                          <Typography variant="caption" color="text.secondary">Market Exposure</Typography>
                                          <Typography
                                            variant="h6"
                                            color={Number(positionRisk.beta_weighted_delta) >= 0 ? 'success.main' : 'error.main'}
                                          >
                                            {currency(Number(positionRisk.beta_weighted_delta))}
                                          </Typography>
                                        </Paper>
                                      </Grid>
                                      <Grid item xs={12} sm={3}>
                                        <Paper variant="outlined" sx={{ p: 1.25 }}>
                                          <Typography variant="caption" color="text.secondary">Current Return</Typography>
                                          <Typography
                                            variant="h6"
                                            color={currentReturnPct === null ? 'text.primary' : (currentReturnPct >= 0 ? 'success.main' : 'error.main')}
                                          >
                                            {currentReturnPct === null ? 'N/A' : `${currentReturnPct >= 0 ? '+' : ''}${currentReturnPct.toFixed(1)}%`}
                                          </Typography>
                                        </Paper>
                                      </Grid>
                                    </Grid>
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                                      Projected P/L vs. hypothetical {position.ticker} move.
                                    </Typography>
                                    <Box sx={{ width: '100%', height: 200 }}>
                                      <ResponsiveContainer>
                                        <BarChart data={graph} margin={{ top: 8, right: 20, left: 8, bottom: 8 }}>
                                          <CartesianGrid strokeDasharray="3 3" stroke={chartGridColor} />
                                          <XAxis
                                            dataKey="move_pct"
                                            height={42}
                                            tick={(tickProps) => {
                                              const { x, y, payload } = tickProps;
                                              const point = graph.find((g) => g.move_pct === payload.value);
                                              const pnl = point ? point.projected_pnl : 0;
                                              const color = point?.isCurrent ? '#f59e0b' : (pnl >= 0 ? '#10b981' : '#ef4444');
                                              return (
                                                <g transform={`translate(${x},${y})`}>
                                                  <text x={0} y={12} textAnchor="middle" fill={color} fontSize={11} fontWeight={700}>
                                                    {`${payload.value > 0 ? '+' : ''}${payload.value}%`}
                                                  </text>
                                                  <text x={0} y={26} textAnchor="middle" fill={color} fontSize={10}>
                                                    {currency(pnl)}
                                                  </text>
                                                </g>
                                              );
                                            }}
                                            axisLine={{ stroke: chartGridColor }}
                                            tickLine={{ stroke: chartGridColor }}
                                          />
                                          <YAxis
                                            tickFormatter={(value) => currency(Number(value))}
                                            width={86}
                                            tick={{ fill: chartAxisColor, fontSize: 11, fontWeight: 600 }}
                                            axisLine={{ stroke: chartGridColor }}
                                            tickLine={{ stroke: chartGridColor }}
                                          />
                                          <Tooltip
                                            formatter={(value, _name, item) => [
                                              currency(Number(value)),
                                              item?.payload?.isCurrent ? 'Current P/L' : 'Projected P/L',
                                            ]}
                                            labelFormatter={(label, items) => {
                                              const isCurrent = items?.[0]?.payload?.isCurrent;
                                              const prefix = isCurrent ? 'Current' : `${position.ticker}`;
                                              return `${prefix} ${label > 0 ? '+' : ''}${label}%`;
                                            }}
                                            contentStyle={{ backgroundColor: chartTooltipBg, border: `1px solid ${chartGridColor}`, borderRadius: 8 }}
                                          />
                                          <ReferenceLine y={0} stroke={chartGridColor} />
                                          <Bar dataKey="projected_pnl" name="Projected P/L">
                                            {graph.map((point) => (
                                              <Cell
                                                key={`position-risk-bar-${position.ticker}-${point.move_pct}`}
                                                fill={point.isCurrent ? '#f59e0b' : (point.projected_pnl >= 0 ? '#10b981' : '#ef4444')}
                                              />
                                            ))}
                                          </Bar>
                                        </BarChart>
                                      </ResponsiveContainer>
                                    </Box>
                                  </>
                                )}
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    );
                  })}
                  {positions.length > 0 && (
                    <TableRow>
                      <TableCell sx={{ fontWeight: 800, fontSize: '1rem' }}>Total</TableCell>
                      <TableCell sx={{ fontWeight: 800, fontSize: '1rem' }}>-</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 800, fontSize: '1rem' }}>{number(positionTotals.quantity, 6)}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 800, fontSize: '1rem' }}>-</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 800, fontSize: '1rem' }}>-</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 800, fontSize: '1rem' }}>{currency(positionTotals.marketValue)}</TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          fontWeight: 800,
                          fontSize: '1rem',
                          color: positionTotals.unrealizedPnl >= 0 ? 'success.main' : 'error.main',
                        }}
                      >
                        {currency(positionTotals.unrealizedPnl)}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          fontWeight: 800,
                          fontSize: '1rem',
                          color: positionTotals.realizedPnl >= 0 ? 'success.main' : 'error.main',
                        }}
                      >
                        {currency(positionTotals.realizedPnl)}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          <Paper sx={{ p: 2, mb: 2 }}>
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              justifyContent="space-between"
              alignItems={{ xs: 'flex-start', md: 'center' }}
              spacing={1}
              sx={{ mb: 1 }}
            >
              <Box>
                <Typography variant="h6">Position Price Trends</Typography>
                <Typography variant="body2" color="text.secondary">
                  Share price history since each current position was opened.
                </Typography>
              </Box>
              <ToggleButtonGroup
                exclusive
                size="small"
                value={positionPriceMode}
                onChange={(_event, nextMode) => { if (nextMode) setPositionPriceMode(nextMode); }}
                aria-label="position price display mode"
              >
                <ToggleButton value="normalized">Indexed to 100</ToggleButton>
                <ToggleButton value="absolute">Absolute values</ToggleButton>
              </ToggleButtonGroup>
            </Stack>
            {positionPriceError && <Alert severity="warning" sx={{ mb: 1.5 }}>{positionPriceError}</Alert>}
            {positionPriceLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 320, gap: 1.5 }}>
                <CircularProgress size={22} />
                <Typography variant="body2">Loading position prices...</Typography>
              </Box>
            ) : positionPriceChartData.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No position price history available.
              </Typography>
            ) : (
              <Box sx={{ width: '100%', height: { xs: 360, md: 460 } }}>
                <ResponsiveContainer>
                  <LineChart data={positionPriceChartData} margin={{ top: 8, right: 24, left: 8, bottom: 18 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={chartGridColor} />
                    <XAxis
                      dataKey="date"
                      minTickGap={40}
                      tickFormatter={(value) => new Date(`${value}T00:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                      tick={{ fill: chartAxisColor, fontSize: 12, fontWeight: 600 }}
                      axisLine={{ stroke: chartGridColor }}
                      tickLine={{ stroke: chartGridColor }}
                    />
                    <YAxis
                      domain={['auto', 'auto']}
                      width={positionPriceMode === 'normalized' ? 64 : 96}
                      tickFormatter={(value) => positionPriceMode === 'normalized' ? Number(value).toFixed(0) : currency(Number(value))}
                      tick={{ fill: chartAxisColor, fontSize: 12, fontWeight: 600 }}
                      axisLine={{ stroke: chartGridColor }}
                      tickLine={{ stroke: chartGridColor }}
                    />
                    <Tooltip
                      itemSorter={(item) => Number.isFinite(Number(item?.value)) ? -Number(item.value) : Number.POSITIVE_INFINITY}
                      formatter={(value) => positionPriceMode === 'normalized' ? Number(value).toFixed(2) : currency(Number(value))}
                      labelFormatter={(label) => new Date(`${label}T00:00:00`).toLocaleDateString()}
                      contentStyle={{ backgroundColor: chartTooltipBg, border: `1px solid ${chartGridColor}`, borderRadius: 8 }}
                    />
                    <Legend verticalAlign="bottom" align="center" iconType="line" wrapperStyle={{ paddingTop: 12 }} />
                    {positionTickers.map((positionTicker, index) => (
                      <Line
                        key={`position-price-${positionTicker}`}
                        type="monotone"
                        dataKey={positionTicker}
                        name={positionTicker}
                        stroke={POSITION_PRICE_COLORS[index % POSITION_PRICE_COLORS.length]}
                        strokeWidth={2}
                        dot={false}
                        connectNulls
                      />
                    ))}
                  </LineChart>
                </ResponsiveContainer>
              </Box>
            )}
          </Paper>

          <Paper sx={{ p: 2, mb: 2 }}>
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              justifyContent="space-between"
              alignItems={{ xs: 'flex-start', md: 'center' }}
              spacing={1}
              sx={{ mb: 1 }}
            >
              <Typography variant="h6">Portfolio History</Typography>
              <ToggleButtonGroup
                value={chartMode}
                exclusive
                size="small"
                onChange={handleChartModeChange}
                aria-label="chart metric"
              >
                <ToggleButton value="equity">Total Equity</ToggleButton>
                <ToggleButton value="market_value">Market Value</ToggleButton>
                <ToggleButton value="balance">Cash</ToggleButton>
              </ToggleButtonGroup>
            </Stack>
            {chartData.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No portfolio history yet. Add a simulated position update to start tracking.
              </Typography>
            ) : (
              <Box sx={{ width: '100%', height: 260 }}>
                <ResponsiveContainer>
                  <AreaChart data={chartData} margin={{ top: 8, right: 20, left: 8, bottom: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={chartGridColor} />
                    <XAxis
                      dataKey="label"
                      minTickGap={40}
                      tick={{ fill: chartAxisColor, fontSize: 12, fontWeight: 600 }}
                      axisLine={{ stroke: chartGridColor }}
                      tickLine={{ stroke: chartGridColor }}
                    />
                    <YAxis
                      tickFormatter={(value) => currency(Number(value))}
                      width={96}
                      tick={{ fill: chartAxisColor, fontSize: 12, fontWeight: 600 }}
                      axisLine={{ stroke: chartGridColor }}
                      tickLine={{ stroke: chartGridColor }}
                    />
                    <Tooltip
                      formatter={(value) => currency(Number(value))}
                      labelFormatter={(label) => String(label)}
                      contentStyle={{
                        backgroundColor: chartTooltipBg,
                        border: `1px solid ${chartGridColor}`,
                        borderRadius: 8,
                      }}
                    />
                    <Area
                      type="monotone"
                      dataKey={selectedMetric.key}
                      stroke={selectedMetric.color}
                      strokeWidth={2}
                      fill={selectedMetric.color}
                      fillOpacity={0.2}
                      dot={false}
                      name={selectedMetric.label}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </Box>
            )}
          </Paper>

          <Paper sx={{ p: 2, mb: 2 }}>
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              justifyContent="space-between"
              alignItems={{ xs: 'flex-start', md: 'center' }}
              spacing={1}
              sx={{ mb: 1.5 }}
            >
              <Box>
                <Typography variant="h6">Portfolio Competition</Typography>
                <Typography variant="body2" color="text.secondary">
                  Compare your paper portfolio with someone you follow.
                </Typography>
              </Box>

              <Select
                size="small"
                displayEmpty
                value={selectedCompetitorUserId}
                onChange={(event) => setSelectedCompetitorUserId(String(event.target.value || ''))}
                sx={{ minWidth: 300, maxWidth: '100%' }}
              >
                <MenuItem value="">
                  <em>Select a followed user</em>
                </MenuItem>
                {followingEntries.map((entry) => (
                  <MenuItem key={`competitor-${entry.user_id}`} value={entry.user_id}>
                    {identityLabel(entry)}
                  </MenuItem>
                ))}
              </Select>
            </Stack>

            {competitionError && (
              <Alert severity="warning" sx={{ mb: 1.5 }}>
                {competitionError}
              </Alert>
            )}

            {competitionLoading ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, py: 2 }}>
                <CircularProgress size={20} />
                <Typography>Loading competition metrics...</Typography>
              </Box>
            ) : !competitorEntry ? (
              <Typography variant="body2" color="text.secondary">
                Follow at least one user and select them to compare portfolios.
              </Typography>
            ) : (
              <>
                <Grid container spacing={1.5} sx={{ mb: 1.5 }}>
                  <Grid item xs={12} md={4}>
                    <Paper variant="outlined" sx={{ p: 1.5 }}>
                      <Typography variant="caption" color="text.secondary">Your Equity</Typography>
                      <Typography variant="h6">{currency(Number(myCompetitionSummary?.metrics?.equity))}</Typography>
                      <Typography variant="body2" color="text.secondary">Return: {percent(myReturnPct)}</Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Paper variant="outlined" sx={{ p: 1.5 }}>
                      <Typography variant="caption" color="text.secondary">{identityLabel(competitorEntry)} Equity</Typography>
                      <Typography variant="h6">{currency(Number(competitorSummary?.metrics?.equity))}</Typography>
                      <Typography variant="body2" color="text.secondary">Return: {percent(competitorReturnPct)}</Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Paper variant="outlined" sx={{ p: 1.5 }}>
                      <Typography variant="caption" color="text.secondary">Equity Gap (You - Competitor)</Typography>
                      <Typography
                        variant="h6"
                        color={
                          Number(myCompetitionSummary?.metrics?.equity) - Number(competitorSummary?.metrics?.equity) >= 0
                            ? 'success.main'
                            : 'error.main'
                        }
                      >
                        {currency(Number(myCompetitionSummary?.metrics?.equity) - Number(competitorSummary?.metrics?.equity))}
                      </Typography>
                    </Paper>
                  </Grid>
                </Grid>

                {competitionChartData.length > 1 ? (
                  <Box sx={{ width: '100%', height: 250, mb: 1.5 }}>
                    <ResponsiveContainer>
                      <AreaChart data={competitionChartData} margin={{ top: 8, right: 20, left: 8, bottom: 8 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke={chartGridColor} />
                        <XAxis
                          dataKey="label"
                          minTickGap={40}
                          tick={{ fill: chartAxisColor, fontSize: 12, fontWeight: 600 }}
                          axisLine={{ stroke: chartGridColor }}
                          tickLine={{ stroke: chartGridColor }}
                        />
                        <YAxis
                          tickFormatter={(value) => currency(Number(value))}
                          width={96}
                          tick={{ fill: chartAxisColor, fontSize: 12, fontWeight: 600 }}
                          axisLine={{ stroke: chartGridColor }}
                          tickLine={{ stroke: chartGridColor }}
                        />
                        <Tooltip
                          formatter={(value) => currency(Number(value))}
                          labelFormatter={(label) => String(label)}
                          contentStyle={{
                            backgroundColor: chartTooltipBg,
                            border: `1px solid ${chartGridColor}`,
                            borderRadius: 8,
                          }}
                        />
                        <Area
                          type="monotone"
                          dataKey="me"
                          stroke="#2563eb"
                          strokeWidth={2}
                          fill="#2563eb"
                          fillOpacity={0.16}
                          dot={false}
                          name="You"
                        />
                        <Area
                          type="monotone"
                          dataKey="competitor"
                          stroke="#f59e0b"
                          strokeWidth={2}
                          fill="#f59e0b"
                          fillOpacity={0.14}
                          dot={false}
                          name={identityLabel(competitorEntry)}
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                    Not enough history yet to draw a competition trend line.
                  </Typography>
                )}
              </>
            )}

            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
              Leaderboard
            </Typography>
            {competitionLeaderboard.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No leaderboard entries yet. Follow more users to compare returns.
              </Typography>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Rank</TableCell>
                      <TableCell>User</TableCell>
                      <TableCell align="right">Equity</TableCell>
                      <TableCell align="right">Total P/L</TableCell>
                      <TableCell align="right">Return %</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {competitionLeaderboard.map((entry) => {
                      const isMe = String(entry?.user_id || '').trim() === String(userId || '').trim();
                      return (
                        <TableRow key={`leaderboard-${entry.user_id}`} selected={isMe}>
                          <TableCell>#{entry.rank}</TableCell>
                          <TableCell>{identityLabel(entry)}{isMe ? ' (You)' : ''}</TableCell>
                          <TableCell align="right">{currency(Number(entry.equity))}</TableCell>
                          <TableCell
                            align="right"
                            sx={{ color: Number(entry.total_pnl) >= 0 ? 'success.main' : 'error.main' }}
                          >
                            {currency(Number(entry.total_pnl))}
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{ color: Number(entry.return_pct) >= 0 ? 'success.main' : 'error.main' }}
                          >
                            {percent(Number(entry.return_pct))}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>

          <Paper sx={{ p: 2, mb: 2 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
              <Typography variant="h6">Pending Orders</Typography>
              <Typography variant="body2" color="text.secondary">
                {pendingLimitOrders.length} open
              </Typography>
            </Stack>

            {pendingLimitOrders.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No pending limit orders.
              </Typography>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Time</TableCell>
                      <TableCell>Ticker</TableCell>
                      <TableCell>Side</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell align="right">Qty</TableCell>
                      <TableCell align="right">Condition</TableCell>
                      <TableCell align="right">Total</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {pendingLimitOrders.map((order) => (
                      <TableRow key={`pending-${order.id}`}>
                        <TableCell>{formatDateTimeInAppTimeZone(order.created_at)}</TableCell>
                        <TableCell>{order.ticker}</TableCell>
                        <TableCell>{String(order.side || '').toUpperCase()}</TableCell>
                        <TableCell>{String(order.order_type || '').toUpperCase().replace('_', '-')}</TableCell>
                        <TableCell align="right">{number(Number(order.quantity), 6)}</TableCell>
                        <TableCell align="right">{orderConditionLabel(order)}</TableCell>
                        <TableCell align="right">{currency(calculateOrderTotal(order))}</TableCell>
                        <TableCell align="right">
                          <Stack direction="row" spacing={1} justifyContent="flex-end">
                            <Button
                              size="small"
                              variant="outlined"
                              disabled={editingOrderId === Number(order.id) || cancellingOrderId === Number(order.id) || order.order_type === 'trailing_stop'}
                              onClick={() => handleEditPendingOrder(order)}
                            >
                              {editingOrderId === Number(order.id) ? 'Saving...' : 'Edit'}
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              color="warning"
                              disabled={cancellingOrderId === Number(order.id) || editingOrderId === Number(order.id)}
                              onClick={() => handleCancelPendingOrder(order.id)}
                            >
                              {cancellingOrderId === Number(order.id) ? 'Cancelling...' : 'Cancel'}
                            </Button>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>

          <Paper sx={{ mb: 2 }}>
            <Box sx={{ px: 2, pt: 2 }}>
              <Typography variant="h6">Portfolio Insights</Typography>
              <Typography variant="body2" color="text.secondary">
                Manage alert triggers and thesis notes without leaving the Portfolio tab.
              </Typography>
            </Box>
            <Tabs
              value={portfolioInsightTab}
              onChange={handleInsightTabChange}
              sx={{ px: 1.5, pt: 1 }}
            >
              <Tab value="alerts" label="Alerts" />
              <Tab value="thesis" label={`Thesis (${cleanTicker})`} />
              <Tab value="risk" label="Market Risk" />
            </Tabs>
            <Divider />
            <Box sx={{ p: 2 }}>
              {portfolioInsightTab === 'alerts' && <PriceAlerts initialTicker={cleanTicker} />}
              {portfolioInsightTab === 'thesis' && <TickerNotes ticker={cleanTicker} />}
              {portfolioInsightTab === 'risk' && (
                riskLoading ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, py: 2 }}>
                    <CircularProgress size={20} />
                    <Typography>Loading risk analysis...</Typography>
                  </Box>
                ) : riskError ? (
                  <Alert severity="warning">{riskError}</Alert>
                ) : !riskAnalysis || !riskAnalysis.positions?.length ? (
                  <Typography variant="body2" color="text.secondary">
                    No open positions to analyze. Market risk needs at least one position.
                  </Typography>
                ) : (
                  <>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                      Market exposure estimates how strongly your portfolio may move with the overall market.
                      A higher dollar amount means larger potential gains or losses when SPY moves.
                    </Typography>
                    <Grid container spacing={1.5} sx={{ mb: 2 }}>
                      <Grid item xs={12} sm={6}>
                        <Paper variant="outlined" sx={{ p: 1.5 }}>
                          <Typography variant="caption" color="text.secondary">Market Exposure</Typography>
                          <Typography
                            variant="h6"
                            color={Number(riskAnalysis.total_beta_weighted_delta) >= 0 ? 'success.main' : 'error.main'}
                          >
                            {currency(Number(riskAnalysis.total_beta_weighted_delta))}
                          </Typography>
                        </Paper>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Paper variant="outlined" sx={{ p: 1.5 }}>
                          <Typography variant="caption" color="text.secondary">Portfolio Equity</Typography>
                          <Typography variant="h6">{currency(Number(riskAnalysis.equity))}</Typography>
                        </Paper>
                      </Grid>
                    </Grid>

                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                      Projected P/L vs. Hypothetical SPY Move
                    </Typography>
                    <Box sx={{ width: '100%', height: 220, mb: 2 }}>
                      <ResponsiveContainer>
                        <BarChart data={riskAnalysis.risk_graph} margin={{ top: 8, right: 20, left: 8, bottom: 8 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke={chartGridColor} />
                          <XAxis
                            dataKey="spy_move_pct"
                            tickFormatter={(value) => `${value > 0 ? '+' : ''}${value}%`}
                            tick={{ fill: chartAxisColor, fontSize: 12, fontWeight: 600 }}
                            axisLine={{ stroke: chartGridColor }}
                            tickLine={{ stroke: chartGridColor }}
                          />
                          <YAxis
                            tickFormatter={(value) => currency(Number(value))}
                            width={90}
                            tick={{ fill: chartAxisColor, fontSize: 12, fontWeight: 600 }}
                            axisLine={{ stroke: chartGridColor }}
                            tickLine={{ stroke: chartGridColor }}
                          />
                          <Tooltip
                            formatter={(value) => currency(Number(value))}
                            labelFormatter={(label) => `SPY ${label > 0 ? '+' : ''}${label}%`}
                            contentStyle={{ backgroundColor: chartTooltipBg, border: `1px solid ${chartGridColor}`, borderRadius: 8 }}
                          />
                          <ReferenceLine y={0} stroke={chartGridColor} />
                          <Bar dataKey="projected_pnl" name="Projected P/L">
                            {riskAnalysis.risk_graph.map((point) => (
                              <Cell key={`risk-bar-${point.spy_move_pct}`} fill={point.projected_pnl >= 0 ? '#10b981' : '#ef4444'} />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </Box>

                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                      Position Market Exposure
                    </Typography>
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Ticker</TableCell>
                            <TableCell align="right">Market Value</TableCell>
                            <TableCell align="right">Market Sensitivity</TableCell>
                            <TableCell align="right">Market Exposure</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {riskAnalysis.positions.map((position) => (
                            <TableRow key={`risk-${position.ticker}`}>
                              <TableCell>{position.ticker}</TableCell>
                              <TableCell align="right">{currency(Number(position.market_value))}</TableCell>
                              <TableCell align="right">{number(Number(position.beta), 2)}</TableCell>
                              <TableCell
                                align="right"
                                sx={{ color: Number(position.beta_weighted_delta) >= 0 ? 'success.main' : 'error.main' }}
                              >
                                {currency(Number(position.beta_weighted_delta))}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </>
                )
              )}
            </Box>
          </Paper>

          <Paper sx={{ p: 2 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
              <Typography variant="h6">Recent Activity</Typography>
              <Button
                size="small"
                endIcon={activityExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                onClick={() => setActivityExpanded((prev) => !prev)}
              >
                {activityExpanded ? 'Collapse' : `Expand (${orders.length})`}
              </Button>
            </Stack>
            <Divider sx={{ mb: 1 }} />
            {!activityExpanded && (
              <Typography variant="body2" color="text.secondary" sx={{ py: 0.5 }}>
                Collapsed to save space. Expand to view the most recent orders.
              </Typography>
            )}
            <Collapse in={activityExpanded} timeout="auto" unmountOnExit>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Time</TableCell>
                      <TableCell>Ticker</TableCell>
                      <TableCell>Side</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell align="right">Qty</TableCell>
                      <TableCell align="right">Condition</TableCell>
                      <TableCell align="right">Fill Price</TableCell>
                      <TableCell align="right">Fees</TableCell>
                      <TableCell align="right">Total</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right">Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {orders.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={11} align="center">No activity yet</TableCell>
                      </TableRow>
                    )}
                    {orders.map((order) => (
                      <TableRow key={order.id}>
                        <TableCell>{formatDateTimeInAppTimeZone(order.created_at)}</TableCell>
                        <TableCell>{order.ticker}</TableCell>
                        <TableCell>{String(order.side || '').toUpperCase()}</TableCell>
                        <TableCell>{String(order.order_type || '').toUpperCase().replace('_', '-')}</TableCell>
                        <TableCell align="right">{number(Number(order.quantity), 6)}</TableCell>
                        <TableCell align="right">{orderConditionLabel(order)}</TableCell>
                        <TableCell align="right">{currency(Number(order.filled_price))}</TableCell>
                        <TableCell align="right">
                          {order.status === 'filled'
                            ? currency((Number(order.commission) || 0) + (Number(order.regulatory_fees) || 0))
                            : '-'}
                        </TableCell>
                        <TableCell align="right">{currency(calculateOrderTotal(order))}</TableCell>
                        <TableCell>{order.status}</TableCell>
                        <TableCell align="right">
                          {order.status === 'pending' && PAPER_PENDING_ORDER_TYPES.includes(String(order.order_type || '').toLowerCase()) ? (
                            <Stack direction="row" spacing={1} justifyContent="flex-end">
                              <Button
                                size="small"
                                variant="outlined"
                                disabled={editingOrderId === Number(order.id) || cancellingOrderId === Number(order.id) || order.order_type === 'trailing_stop'}
                                onClick={() => handleEditPendingOrder(order)}
                              >
                                {editingOrderId === Number(order.id) ? 'Saving...' : 'Edit'}
                              </Button>
                              <Button
                                size="small"
                                variant="outlined"
                                color="warning"
                                disabled={cancellingOrderId === Number(order.id) || editingOrderId === Number(order.id)}
                                onClick={() => handleCancelPendingOrder(order.id)}
                              >
                                {cancellingOrderId === Number(order.id) ? 'Cancelling...' : 'Cancel'}
                              </Button>
                            </Stack>
                          ) : '-'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Collapse>
          </Paper>
        </>
      )}
    </Box>
  );
};

export default PaperTrading;
